"use client";

import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Search, Notifications } from "@mui/icons-material";
// import Image from 'next/image'
import ProfileParent from "./ConnectParent/ProfileParent";
import { useUserProfileContext } from "./Layout";
import { useUser } from "@/contexts/UserContext";
import { useEffect, useState } from "react";
import { PaperclipIcon } from "lucide-react";
import { sanitizeInputName } from "@/utils/sanitizeInput";
import { validateEmail, validateFirstnameLastname } from "@/utils/validation";
import SelectWithSearch from "@/components/SelectWithSearch";
import { apiRemote, ICity, IUserProfile } from "@/api";
import { useAvatar } from "@/contexts/AvatarContext";
import { useAlert } from "@/contexts/AlertContext";
import SchoolSearchDialog from "./FilterSchoolDialog";

const generateYearOptions = (): string[] => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 2000; year <= currentYear; year++) {
    years.push(year.toString());
  }
  return years;
};

export default function UserProfile() {
  var { user } = useUser();
  const { avatar, setAvatar } = useAvatar();
  const { getUserProfile, countries } = useUserProfileContext();
  const [editProfile, setEditProfile] = useState<IUserProfile>({} as any);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [cities, setCities] = useState<ICity[]>([]);
  const alert = useAlert();
  const [open, setOpen] = useState(false);
  const [cityName, setCityName] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [profile, setProfile] = useState<IUserProfile>({} as any);
  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true); // Bắt đầu tải dữ liệu
      try {
        const res = await getUserProfile();
        setProfile(res);
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  useEffect(() => {
    if (user?.avatar_url) {
      setAvatar(user?.avatar_url);
    }
  }, [user, setAvatar]);

  useEffect(() => {
    if (profile) {
      if (profile.country_id) {
        fetchCities(profile.country_id);
      }
      setEditProfile(profile);
    }
  }, [profile]);

  useEffect(() => {
    if (!profile || !cities) return;
  
    const selectedOption = cities.find(
      (item) =>
        profile.city_id !== null &&
        profile.city_id !== undefined &&
        item.id.toString() === profile.city_id.toString()
    );
  
    const label = selectedOption ? selectedOption.name : "";
    setCityName(label);
    setEditProfile((prev) => ({
      ...profile,
      city_name: label, // Lưu giá trị ban đầu
      email_old: profile.email,
      country_id: prev.country_id,
      school_name: prev.school_name,
      school_id: prev.school_id,
    }));
  }, [cities, profile]);

  const days = Array.from({ length: 31 }, (_, i) => (i + 1).toString());
  const months = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
  const years = generateYearOptions();

  const fetchCities = async (countryId: string) => {
    try {
      const response = await apiRemote.getCityByCountry(countryId);
      if (response && Array.isArray(response)) {
        // Kiểm tra response có phải là mảng không
        const newCities: ICity[] = response;
        setCities(newCities);
        setEditProfile((prev) => ({
          ...prev,
          listCity: newCities,
          country_id: countryId, // Set newCities as listCity
        }));
      } else {
        setCities([]); // Reset nếu không có dữ liệu
      }
    } catch (error) {
      console.error("Failed to fetch cities:", error);
      setCities([]); // Reset danh sách nếu xảy ra lỗi
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validExtensions = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validExtensions.includes(file.type)) {
        alert.error('Only accept files in .jpg, .jpeg, .png formats');
        return;
      }
    }

    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("avatar", file);

      apiRemote
        .uploadAvatar({
          avatar: file,
        })
        .then((res) => {
          alert.success(res.update_message);
          if (res.avatar_url != "") {
            setAvatar(res.avatar_url);
          }
        })
        .catch((err) => {
          console.error("Update failed:", err);
        });
    } catch (error) {
      console.error("Error updating avatar:", error);
    } finally {
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (Object.values(errors).some((error) => error)) {
      alert.error("Please fix the errors before submitting.");
      return;
    }

    // console.log(editProfile);
    setIsSubmitting(true);
    apiRemote
      .updateProfile({
        email: editProfile.email,
        email_old: editProfile.email_old,
        username: editProfile.username,
        day: editProfile.day,
        month: editProfile.month,
        year: editProfile.year,
        school_name: editProfile.school_name,
        city_id: editProfile.city_id,
        country_id: editProfile.country_id,
        first_name: editProfile.firstname,
        last_name: editProfile.lastname,
        idUserMD: editProfile.idUserMD,
      })
      .then((res) => {
        if (res.code == 200) {
          alert.success("Success");
        } else {
          alert.error(res.message);
        }
      })
      .catch((err) => {
        alert.error(err.message);
        throw err;
      })
      .finally(() => {
        // alert.hideWait();
        setIsSubmitting(false)
      });
  };

  const handleSchoolSelection = (id: any, name: any) => {
    setEditProfile((prev) => ({
      ...prev,
      school_id: id.toString(),
      school_name: name.toString(),
    }));
  };

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "white",
        borderRadius: 2,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        flex: 1,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
        <Typography variant="h6" style={{color: "rgb(15, 105, 185)"}}>Your Profile</Typography>
      </Box>

      {loading ? (
        <Box
          sx={{
            p: 3,
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 1,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Add a profile picture:
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: 320,
                  mx: "auto",
                }}
              >
                <img
                  src={avatar}
                  alt="Profile picture"
                  style={{ width: 320, height: 200, objectFit: "cover" }}
                />
                <label
                  htmlFor="avatar-upload"
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "#16f816",
                    color: "primary.main",
                    cursor: "pointer",
                    padding: "5px",
                    borderRadius: "10px",
                  }}
                >
                  <Typography style={{ textTransform: "none" }}>
                    Change avatar
                  </Typography>
                  <input
                    id="avatar-upload"
                    style={{ display: "none" }}
                    type="file"
                    name="avatarFile"
                    accept=".jpeg,.jpg,.png"
                    onChange={handleFileChange}
                  />
                </label>
              </Box>
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                textAlign="center"
                mt={1}
              >
                Supported image formats: jpg, png
                <br />
                Maximum file size: 100KB
              </Typography>
            </Box>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <Typography variant="body1" color="textSecondary">
                      First Name:{" "}
                      <span style={{ color: "red", fontWeight: "bold" }}>
                        *
                      </span>
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      name="firstname"
                      value={editProfile.firstname || ""}
                      onChange={(event) => {
                        const value = event.target.value;
                        let sanitizedValue = sanitizeInputName(
                          value.toString()
                        );
                        setEditProfile({
                          ...editProfile,
                          firstname: sanitizedValue,
                        });
                        const firstNameError =
                          validateFirstnameLastname(sanitizedValue);
                        setErrors((prev) => ({
                          ...prev,
                          firstname: firstNameError,
                        }));
                      }}
                      error={!!errors.firstname} // Hiển thị viền đỏ nếu có lỗi
                      helperText={errors.firstname}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth margin="normal">
                    <Typography variant="body1" color="textSecondary">
                      Last Name:{" "}
                      <span style={{ color: "red", fontWeight: "bold" }}>
                        *
                      </span>
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      name="lastname"
                      value={editProfile.lastname || ""}
                      onChange={(event) => {
                        const value = event.target.value;
                        let sanitizedValue = sanitizeInputName(
                          value.toString()
                        );
                        setEditProfile({
                          ...editProfile,
                          lastname: sanitizedValue,
                        });
                        const lastNameError =
                          validateFirstnameLastname(sanitizedValue);
                        setErrors((prev) => ({
                          ...prev,
                          lastname: lastNameError,
                        }));
                      }}
                      error={!!errors.lastname} // Hiển thị viền đỏ nếu có lỗi
                      helperText={errors.lastname}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <Typography variant="body1" color="textSecondary">
                      Username:
                    </Typography>
                    <TextField
                      fullWidth
                      disabled
                      name="username"
                      value={editProfile.username || ""}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <Typography variant="body1" color="textSecondary">
                      Password:
                    </Typography>
                    <TextField
                      fullWidth
                      disabled
                      name="username"
                      type="password"
                      value={editProfile.password || ""}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              color="primary"
                              sx={{ textTransform: "none" }}
                              onClick={() => {
                                window.open(
                                  process.env.URL_CHANGE_PASSWORD,
                                  "_blank"
                                );
                              }}
                            >
                              Change password
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <Typography variant="body1" color="textSecondary">
                      Email Address:{" "}
                      <span style={{ color: "red", fontWeight: "bold" }}>
                        *
                      </span>
                    </Typography>
                    <TextField
                      required
                      fullWidth
                      name="email"
                      type="email"
                      value={editProfile.email || ""}
                      onChange={(event) => {
                        const value = event.target.value;
                        setEditProfile({
                          ...editProfile,
                          email: value.toString(),
                        });
                        const error = validateEmail(value.toString());
                        setErrors((prev) => ({ ...prev, email: error }));
                      }}
                      error={!!errors.email} // Hiển thị viền đỏ nếu có lỗi
                      helperText={errors.email}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <Typography variant="body1" color="textSecondary">
                      Date of Birth:{" "}
                      <span style={{ color: "red", fontWeight: "bold" }}>
                        *
                      </span>
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <SelectWithSearch
                          options={days.map((item) => ({
                            value: item.toString(),
                            label: item,
                          }))}
                          label={""}
                          value={editProfile.day?.toString()}
                          required={true}
                          disabled={false}
                          onChange={(value) => {
                            setEditProfile({
                              ...editProfile,
                              day: value.value.toString(),
                            });
                          }}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <SelectWithSearch
                          options={months.map((item) => ({
                            value: item.toString(),
                            label: item,
                          }))}
                          label={""}
                          value={editProfile.month}
                          required={true}
                          disabled={false}
                          onChange={(value) => {
                            setEditProfile({
                              ...editProfile,
                              month: value.value.toString(),
                            });
                          }}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <SelectWithSearch
                          options={years.map((item) => ({
                            value: item.toString(),
                            label: item,
                          }))}
                          label={""}
                          value={editProfile.year}
                          required={true}
                          disabled={false}
                          onChange={(value) => {
                            setEditProfile({
                              ...editProfile,
                              year: value.value.toString(),
                            });
                          }}
                        />
                      </Grid>
                    </Grid>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body1" color="textSecondary">
                    Country:{" "}
                    <span style={{ color: "red", fontWeight: "bold" }}>*</span>
                  </Typography>
                  <SelectWithSearch
                    options={countries.map((item) => ({
                      value: item.id.toString(),
                      label: item.country_name,
                    }))}
                    label={""}
                    value={editProfile.country_id}
                    required={true}
                    disabled={false}
                    onChange={(value) => {
                      setEditProfile({
                        ...editProfile,
                        country_id: value.value.toString(),
                        school_name: "",
                        school_id: "",
                      });
                      fetchCities(value.value.toString());
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" color="textSecondary">
                    State/City/Province:{" "}
                    <span style={{ color: "red", fontWeight: "bold" }}>*</span>
                  </Typography>
                  <SelectWithSearch
                    options={cities.map((item) => ({
                      value: item.id.toString(),
                      label: item.name,
                    }))}
                    label={""}
                    value={editProfile.city_id}
                    required={true}
                    disabled={false}
                    onChange={(value) => {
                      const selectedOption = cities.find(
                        (item) => item.id.toString() === value.value.toString()
                      );
                      const label = selectedOption ? selectedOption.name : "";

                      setCityName(label);

                      setEditProfile({
                        ...editProfile,
                        city_id: value.value.toString(),
                        city_name: label,
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" color="textSecondary">
                    School:{" "}
                    <span style={{ color: "red", fontWeight: "bold" }}>*</span>
                  </Typography>
                  <TextField
                    required
                    fullWidth
                    name="school_name"
                    value={editProfile.school_name || ""}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          onClick={() => {
                            setOpen(true);
                          }}
                        >
                          <IconButton>
                            <Search />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    type="submit"
                    color="primary"
                    sx={{ mt: 2 }}
                    disabled={isSubmitting}
                  >
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>

          <ProfileParent initialData={editProfile} />
          <SchoolSearchDialog
            open={open}
            setOpen={setOpen}
            state={cityName}
            onSchoolSelect={(id: any, name: any) =>
              handleSchoolSelection(id, name)
            }
          />
        </>
      )}
    </Box>
  );
}
